/**
 * Resales-Online — Landing / Offer Viewer (entry / orchestrator)
 * ====================================================================================
 * The production offer viewer, served at the site root for a shared link
 * (`/?uuid=…&rsId=…`). It has its own folder, scoped SCSS (`.rs-proto`) and inline
 * SVG icons so it stays self-contained.
 *
 * This file is the **container**: it owns page-level state and wiring and
 * delegates all rendering to small sibling components. Everything lives flat in
 * this one folder — data/logic and presentational components side by side:
 *
 *   Data / logic                         Presentational components
 *   ────────────                         ─────────────────────────
 *   useOffers.js   — fetch feed           Header        OfferTabs     PropertySection
 *   useAudit.js    — audit beacons          └ PropertyInfo (Gallery, PurchaseCosts, …)
 *   mapProperty.js — API → view-model       └ Sidebar     ContactDialog → CountryPhoneField
 *   countries.js   — phone dial codes     FeaturesModal  AgencyBadge   Notice
 *   useGeoCountry  — IP → default country
 *   maps.js        — Google embed URL     Shared: hooks.js (useIsMobile/useClickOutside)
 *   i18n.js        — useStrings()                 icons.js, resalesLanding.scss
 *
 * Responsibilities kept here: loading offers, tracking the active offer,
 * click-to-scroll + scroll-spy, the toast, and which overlays (features modal /
 * contact dialog) are open.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import useOffers, { findOfferIndex } from './useOffers';
import useAudit from './useAudit';
import { useStrings } from './i18n';
import { useIsMobile } from './hooks';
import { telHref, EXTERNAL_LINK } from './contactLinks';
import { IconPhone, IconChevronLeft, IconFacebook, IconInstagram, IconLinkedIn, IconTwitterX } from './icons'; // IconSend unused — contact form disabled
import Header from './Header';
import OfferTabs from './OfferTabs';
import OfferCards from './OfferCards';
import PropertySection from './PropertySection';
// import ContactDialog from './ContactDialog'; // contact form disabled
import FeaturesModal from './FeaturesModal';
import AgencyBadge from './AgencyBadge';
import Notice from './Notice';
import './resalesLanding.scss';

// Detect desktop Firefox on Linux (UA sniff — a cosmetic gate only, with a safe
// default if the guess is wrong). Firefox on Linux (WebRender + NVIDIA) paces the
// slow Ken Burns transform unevenly even on the GPU compositor, so the SCSS
// shortens the animation for just this config (see `rs-ff-linux`).
function isFirefoxLinux() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // Desktop Firefox on Linux reports "X11; Linux"; exclude Android/Mobile,
    // which are also Linux-based but run the animation fine.
    return /Firefox\//.test(ua) && /Linux|X11/.test(ua) && !/Android|Mobile/.test(ua);
}

// Agency social links (from settings) → their branded icon + label.
const SOCIAL_ICONS = [
    ['facebook', IconFacebook, 'Facebook'],
    ['instagram', IconInstagram, 'Instagram'],
    ['linkedin', IconLinkedIn, 'LinkedIn'],
    ['x', IconTwitterX, 'X'],
];

export default function ResalesLanding() {
    const t = useStrings();
    const { offers, agency, settings, initialIndex, scrollToIndex, loading, status, uuid, searchInfo } = useOffers();

    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(null); // cards layout: which property is open (null = grid)
    // List layout: the offer whose section the user has actually reached in the
    // viewport (scroll-spy / tab click / deep link). Unlike activeIndex it is NOT
    // preset to 0 on load, so nothing is counted as "viewed" until it really is.
    const [viewedIndex, setViewedIndex] = useState(null);

    // Layout axes. The cards/grid choice applies on MOBILE too — there the
    // results keep the current mobile card look (offers strip hidden) and each
    // card opens its property as a separate detail page; only desktop swaps the
    // results for the OfferCards grid. The compact section format stays
    // desktop-only (mobile always renders the standard section).
    const isMobile = useIsMobile(767);
    const offersLayout = settings.offersLayout;
    const sectionFormat = isMobile ? 'standard' : settings.sectionFormat;
    const cardsMode = offersLayout === 'cards' && offers.length > 1;

    // Audit beacons: searches + impressions on load; a property-action VIEW only
    // when a property detail is viewed — the cards-layout detail page, a
    // single-property link (its own detail), or, in the list layout, the section
    // the user has scrolled to. Never on the cards results grid. Live data only.
    const detailIndex = cardsMode ? selectedIndex : offers.length === 1 ? 0 : viewedIndex;
    useAudit({ status, uuid, agency, offers, searchInfo, detailIndex });

    const [facilitiesOffer, setFacilitiesOffer] = useState(null); // offer shown in the features modal
    // const [contactOpen, setContactOpen] = useState(false); // contact form disabled
    const [toast, setToast] = useState(null);
    const [stuck, setStuck] = useState(false); // sticky top bar is pinned → shrink the header

    // Footer social links: the agency's own (from settings), falling back to the
    // example set for review. Layout switches to logo/call-left, socials-right
    // whenever any exist.
    const social = (agency && agency.social) || {};
    const footerSocial = SOCIAL_ICONS.filter(([k]) => social[k]);

    const sectionRefs = useRef([]);            // one <section> ref per offer, for scrolling
    const programmatic = useRef(null);         // target index owned during a click-scroll (null otherwise)
    const settleRaf = useRef(0);               // rAF watching for the click-scroll to settle

    // Tag <body> while the prototype is mounted (lets global CSS opt out if needed).
    useEffect(() => {
        document.body.classList.add('rs-proto-active');
        // Firefox on Linux paces the slow Ken Burns transform unevenly (even on
        // the compositor); a shorter cycle hides the jitter. Tag the body so the
        // SCSS applies a faster duration to just that config.
        const ffLinux = isFirefoxLinux();
        if (ffLinux) document.body.classList.add('rs-ff-linux');
        return () => {
            document.body.classList.remove('rs-proto-active');
            if (ffLinux) document.body.classList.remove('rs-ff-linux');
            cancelAnimationFrame(settleRaf.current);
        };
    }, []);

    // The top bar is sticky at top:0; when stuck the CSS shrinks the header.
    // Shrinking the header removes vertical space above the viewport, and the
    // browser's scroll anchoring nudges scrollY to compensate — which, with a
    // single threshold, would re-cross it and grow the header again, oscillating
    // (the shake). Use HYSTERESIS: engage past ~96px, release only below ~24px.
    // The 72px dead-zone is larger than the shrink delta, so one anchoring nudge
    // can't flip the state back. (overflow-anchor:none in the SCSS also disables
    // that compensation as a belt-and-braces measure.)
    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const y = window.pageYOffset;
                setStuck((prev) => (prev ? y > 24 : y > 96));
            });
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
    }, []);

    // Scroll a section to just below the sticky offer strip. Computed explicitly
    // (rather than scrollIntoView + scroll-margin) so it lands consistently, and
    // we briefly mute the scroll-spy so it doesn't re-target mid-animation.
    const scrollToSection = (i) => {
        const el = sectionRefs.current[i];
        if (!el) return;
        // Offset by whatever is actually sticky at the top: the whole top bar on
        // desktop, but only the offer strip below 1024px (there the header scrolls
        // away). Measure it live rather than use a stale magic number. +12px.
        const desktop = typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(min-width: 1024px)').matches;
        const bar = document.querySelector(desktop ? '.rs-topbar' : '.rs-offers');
        const barH = bar ? bar.getBoundingClientRect().height : (isMobile ? 84 : 100);
        const stickyOffset = barH + 12;
        const top = el.getBoundingClientRect().top + window.pageYOffset - stickyOffset;
        // Own the active tab for the WHOLE smooth scroll. The scroll-spy is muted
        // while `programmatic` is set, so sections passing through the viewport on
        // the way to a distant target can't briefly highlight (e.g. flashing offer
        // 11 while scrolling 1 -> 12). A fixed timer was too short for long jumps;
        // instead release only once the scroll actually settles at the target
        // (position stops changing for a few frames) or a safety cap, whichever
        // comes first.
        programmatic.current = i;
        setActiveIndex(i);
        setViewedIndex(i); // navigated section (tab click / deep link) counts as viewed; deduped per RsId
        window.scrollTo({ top, behavior: 'smooth' });
        cancelAnimationFrame(settleRaf.current);
        const startT = Date.now();
        let lastY = window.pageYOffset;
        let stable = 0;
        const watch = () => {
            const y = window.pageYOffset;
            if (Math.abs(y - lastY) < 1) stable += 1; else stable = 0;
            lastY = y;
            const elapsed = Date.now() - startT;
            if ((elapsed > 150 && stable >= 3) || elapsed > 2000) {
                programmatic.current = null;
                setActiveIndex(i); // ensure the target is the final highlight
                return;
            }
            settleRaf.current = requestAnimationFrame(watch);
        };
        settleRaf.current = requestAnimationFrame(watch);
    };

    // Show a transient toast message (auto-hides after 2.6s).
    const showToast = (msg) => {
        setToast(msg);
        window.clearTimeout(showToast._t);
        showToast._t = window.setTimeout(() => setToast(null), 2600);
    };

    // Keep ?ref= in step with whichever property is open, so the address bar is
    // always a shareable link to the current view (and a reload returns to it).
    // Opening a property from the grid PUSHES a history entry, so Back returns to
    // the grid; moving between properties replaces it, so Back is always one step
    // home rather than a walk back through everything visited.
    // "We pushed an entry to get here" is recorded in history.state, not a React
    // ref: it has to survive a reload and a Back->Forward round trip. With an
    // in-memory flag, Forward back onto a pushed entry would leave it cleared, so
    // Back to results would replace instead of stepping back — stranding a
    // duplicate grid entry in the history.
    const cameFromGrid = () => typeof window !== 'undefined' && !!window.history
        && !!window.history.state && window.history.state.pvFromGrid === true;
    const writeRefParam = (offer, push) => {
        if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
        try {
            const url = new URL(window.location.href);
            // ?ref= is the single expression of "which property is open", so the
            // older ?rsId= form is dropped as soon as we take over — otherwise
            // Back to results after an ?rsId= arrival would leave a URL that
            // reopens the property on reload.
            url.searchParams.delete('rsId');
            if (offer && offer.reference) url.searchParams.set('ref', offer.reference);
            else url.searchParams.delete('ref');
            if (push && window.history.pushState) {
                window.history.pushState({ pvFromGrid: true }, '', url.toString());
            } else {
                // Paging between properties keeps the marker (we still arrived from
                // the grid); clearing the param drops it.
                const keep = !!offer && cameFromGrid();
                window.history.replaceState(keep ? { pvFromGrid: true } : null, '', url.toString());
            }
        } catch (e) { /* non-fatal: the view still works without the param */ }
    };

    // Cards layout: open a different property and reset to the top of the page.
    const goToProperty = (i) => {
        const fromGrid = selectedIndex == null;
        setSelectedIndex(i);
        writeRefParam(offers[i], fromGrid);
        window.scrollTo(0, 0);
    };

    // Back to results. When we pushed an entry on the way in, step back through it
    // so the forward button still works and no stale entry is left behind;
    // otherwise (opened straight from a ?ref= link) just clear the param.
    const backToResults = () => {
        if (cameFromGrid()) { window.history.back(); return; } // popstate clears the selection
        setSelectedIndex(null);
        writeRefParam(null, false);
    };

    // The URL is the source of truth for which property is open, so Back/Forward
    // move between the grid and a property rather than leaving the viewer.
    useEffect(() => {
        if (typeof window === 'undefined' || !cardsMode) return undefined;
        const onPopState = () => {
            const params = new URLSearchParams(window.location.search);
            const wantRef = params.get('ref');
            if (!wantRef) { setSelectedIndex(null); return; }
            const found = findOfferIndex(offers, wantRef, null);
            setSelectedIndex(found >= 0 ? found : null);
            window.scrollTo(0, 0);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [cardsMode, offers]);

    // Once data is loaded, mark the initial offer active. Only a ?rsId= deep link
    // (scrollToIndex != null) scrolls to its offer; otherwise the page opens at the
    // top — no auto-scroll to the offers on load, desktop or mobile — and the
    // scroll-spy takes over the active index once the user scrolls.
    useEffect(() => {
        if (loading) return undefined;
        setActiveIndex(initialIndex || 0);
        // Cards layout has no page-scroll: a ?rsId= deep link opens that property
        // directly (else the grid is shown).
        if (cardsMode) {
            setSelectedIndex(scrollToIndex != null ? scrollToIndex : null);
            return undefined;
        }
        if (scrollToIndex == null) return undefined;
        const timer = window.setTimeout(() => scrollToSection(scrollToIndex), 60);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, initialIndex, scrollToIndex, cardsMode]);

    // Scroll-spy: highlight the offer tab for whichever section is in view.
    useEffect(() => {
        if (loading || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (programmatic.current !== null) return; // a click-scroll owns the active tab
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = Number(entry.target.getAttribute('data-index'));
                        if (!Number.isNaN(idx)) {
                            setActiveIndex(idx);
                            setViewedIndex(idx);
                        }
                    }
                });
            },
            { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
        );
        sectionRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [loading, offers.length]);

    // Contact form / dialog submit → confirmation toast. (contact form disabled)
    // const handleSubmit = (cc, form) => {
    //     const who = form.firstName ? form.firstName.trim() : t.yourDetails;
    //     showToast(`${t.enquirySent} ${who} (${cc.code})`);
    // };

    if (loading) {
        return (
            <div className="rs-proto">
                <div className="rs-loading"><Spinner animation="border" /></div>
            </div>
        );
    }

    // An expired or failed link shows a clear message rather than a blank page.
    if (status === 'expired') return <Notice title={t.expiredTitle} body={t.expiredBody} />;
    if (status === 'error') return <Notice title={t.unavailableTitle} body={t.unavailableBody} />;
    // Every property on the link has since sold or gone off market.
    if (status === 'empty') return <Notice title={t.noProperties} body={t.soldOutBody} />;

    return (
        <div className={`rs-proto${cardsMode && selectedIndex == null && !isMobile ? ' rs-proto--cards' : ''}`}>
            {/* Sticky top bar: header (logo + contacts) and the offer strip pin
                together so they never overlap at the top. */}
            <div className={`rs-topbar${stuck ? ' is-stuck' : ''}`}>
                <Header isMobile={isMobile} agency={agency} />

                {/* The offer strip is only useful with multiple stacked offers —
                    hide it for a single-property link and for the cards layout
                    (there the grid itself is the navigator). */}
                {offers.length > 1 && !cardsMode && (
                    <OfferTabs
                        offers={offers}
                        activeIndex={activeIndex}
                        onSelect={(i) => scrollToSection(i)}
                    />
                )}
            </div>

            <main className="rs-main">
                {cardsMode ? (
                    // Cards/grid layout: picking a result opens its property as
                    // its own page with back-to-results + prev/next. Desktop
                    // results are the OfferCards grid; mobile results keep the
                    // familiar stacked mobile cards ('nav' mode — the View
                    // Details button opens the page instead of expanding).
                    selectedIndex == null ? (
                        isMobile ? (
                            offers.map((offer, i) => (
                                <PropertySection
                                    key={offer.id}
                                    index={i}
                                    offer={offer}
                                    settings={settings}
                                    sectionFormat={sectionFormat}
                                    mode="nav"
                                    onOpen={() => goToProperty(i)}
                                    onViewFacilities={() => setFacilitiesOffer(offer)}
                                />
                            ))
                        ) : (
                            <OfferCards offers={offers} onSelect={goToProperty} />
                        )
                    ) : (
                        <div className="rs-cardview">
                            <div className="rs-cardview__bar">
                                <button className="rs-pill rs-pill--primary rs-backlink" onClick={backToResults}>
                                    <IconChevronLeft size={18} /> {t.backToResults}
                                </button>
                                {/* Prev | Next property, right-aligned on the same row. */}
                                <div className="rs-cardnav">
                                    {selectedIndex > 0 && (
                                        <button type="button" className="rs-cardnav__link" onClick={() => goToProperty(selectedIndex - 1)}>
                                            {t.prevProperty}
                                        </button>
                                    )}
                                    {selectedIndex > 0 && selectedIndex < offers.length - 1 && (
                                        <span className="rs-cardnav__sep" aria-hidden="true">|</span>
                                    )}
                                    {selectedIndex < offers.length - 1 && (
                                        <button type="button" className="rs-cardnav__link" onClick={() => goToProperty(selectedIndex + 1)}>
                                            {t.nextProperty}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <PropertySection
                                key={offers[selectedIndex].id}
                                index={selectedIndex}
                                offer={offers[selectedIndex]}
                                settings={settings}
                                sectionFormat={sectionFormat}
                                mode="detail"
                                onViewFacilities={() => setFacilitiesOffer(offers[selectedIndex])}
                            />
                        </div>
                    )
                ) : (
                    // List layout (default): one section per property, all results
                    // on a single scrollable page.
                    offers.map((offer, i) => (
                        <PropertySection
                            key={offer.id}
                            ref={(el) => { sectionRefs.current[i] = el; }}
                            index={i}
                            offer={offer}
                            settings={settings}
                            sectionFormat={sectionFormat}
                            onViewFacilities={() => setFacilitiesOffer(offer)}
                        />
                    ))
                )}
            </main>

            {/* Static footer at the bottom of the page (no longer sticky). With
                social links present it splits: logo + Call on the left, socials
                on the right; without, it stays centred. */}
            <footer className="rs-footer">
                <div className={`rs-footer__inner${footerSocial.length ? ' has-social' : ''}`}>
                    <div className="rs-footer__main">
                        <AgencyBadge agency={agency} />
                        <div className="rs-footer__actions">
                            {/* Contact form disabled — commented out per request.
                            <button className="rs-pill rs-pill--tint" onClick={() => setContactOpen(true)}>
                                <IconSend /> {t.contact}
                            </button>
                            */}
                            {agency.phone ? (
                                <a className="rs-pill rs-pill--primary" href={telHref(agency.phone)}>
                                    <IconPhone /> {t.call}
                                </a>
                            ) : (
                                <button className="rs-pill rs-pill--primary" onClick={() => showToast(t.noPhone)}>
                                    <IconPhone /> {t.call}
                                </button>
                            )}
                        </div>
                    </div>
                    {footerSocial.length > 0 && (
                        <div className="rs-footer__social">
                            {footerSocial.map(([key, Icon, label]) => (
                                <a key={key} className="rs-social-link" href={social[key]} aria-label={label} {...EXTERNAL_LINK}>
                                    <Icon size={25} />
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </footer>

            <FeaturesModal offer={facilitiesOffer} onHide={() => setFacilitiesOffer(null)} />

            {/* Contact form disabled — commented out per request.
            <ContactDialog
                show={contactOpen}
                onHide={() => setContactOpen(false)}
                agency={agency}
                onSubmit={handleSubmit}
            />
            */}

            {toast && <div className="rs-toast">{toast}</div>}
        </div>
    );
}
